import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import emailjs from '@emailjs/browser'; // Import EmailJS

const AITrainerCTA = () => {
  const [primaryHover, setPrimaryHover] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organizationName: '',
    organizationType: '',
    role: '',
    specificRequirements: ''
  });

  // Modal handlers
  const handleShowModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // Reset form data to match the cleaned-up state
    setFormData({
      name: '',
      email: '',
      phone: '',
      organizationName: '',
      organizationType: '',
      role: '',
      specificRequirements: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Updated handleSubmit function to send email with EmailJS
  const handleSubmit = (e) => {
    e.preventDefault();

    // --- IMPORTANT: Replace with your actual EmailJS credentials ---
    const serviceID = 'service_n36e8hg'; // Your EmailJS Service ID
    const templateID = 'template_i9jkfri'; // Your EmailJS Template ID
    const publicKey = 'VAEdCMXuUKeCOw6gb'; // Your EmailJS Public Key

    // Construct the email body from form data
    const emailBody = `
      New AI Trainer Kit Demo Request
      ---------------------------------

      Contact Information:
      - Name: ${formData.name}
      - Email: ${formData.email}
      - Phone: ${formData.phone || 'Not provided'}

      Organization Details:
      - Organization Name: ${formData.organizationName}
      - Organization Type: ${formData.organizationType}
      - Submitter's Role: ${formData.role}

      Specific Requirements:
      - ${formData.specificRequirements || 'No specific requirements were provided.'}
    `;

    // Prepare the parameters for the EmailJS template
    const templateParams = {
      email_subject: `AI Trainer Demo Request from ${formData.organizationName}`,
      email_body: emailBody.trim()
    };
    
    // Send the email using EmailJS
    emailjs.send(serviceID, templateID, templateParams, publicKey)
      .then((response) => {
        console.log('SUCCESS!', response.status, response.text);
        alert('Thank you for your interest! Our team will contact you within 24 hours to schedule your personalized demo.');
        handleCloseModal();
      })
      .catch((err) => {
        console.error('FAILED...', err);
        alert('Failed to send the request. Please try again later or contact us directly.');
      });
  };

  // --- Styles (No changes from original component) ---
  const baseStyles = {
    container: {
      backgroundColor: '#0a0a0a',
      color: 'white',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '80px 24px',
      textAlign: 'center',
      minHeight: '400px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    },
    wrapper: {
      maxWidth: '900px',
      width: '100%'
    },
    title: {
      fontSize: '3.5rem',
      fontWeight: '700',
      lineHeight: '1.1',
      marginBottom: '32px',
      letterSpacing: '-0.02em'
    },
    titleHighlight: {
      background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    subtitle: {
      fontSize: '1.25rem',
      color: '#a1a1aa',
      lineHeight: '1.6',
      marginBottom: '48px',
      fontWeight: '400',
      maxWidth: '700px',
      margin: '0 auto 48px auto'
    },
    buttonGroup: {
      display: 'flex',
      gap: '16px',
      justifyContent: 'center',
      flexWrap: 'wrap'
    }
  };

  const getPrimaryButtonStyle = () => ({
    background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
    color: 'white',
    border: 'none',
    padding: '16px 32px',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '200px',
    justifyContent: 'center',
    transform: primaryHover ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: primaryHover 
      ? '0 6px 25px rgba(79, 70, 229, 0.4)' 
      : '0 4px 20px rgba(79, 70, 229, 0.3)'
  });
  
  const ChartIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M22 21H2v-2h2V5h4v14h4V9h4v10h4V3h4v16h2v2z"/>
    </svg>
  );

  return (
    <>
      <section style={baseStyles.container}>
        <div style={baseStyles.wrapper}>
          <header>
            <h1 style={baseStyles.title}>
              Ready to Transform{' '}
              <span style={baseStyles.titleHighlight}>AI Education</span>?
            </h1>
            
            <p style={baseStyles.subtitle}>
              Join thousands of educators and students already experiencing the future 
              of AI learning with our portable, powerful, and affordable solution.
            </p>
          </header>

          <div style={baseStyles.buttonGroup}>
            <button 
              style={getPrimaryButtonStyle()}
              onMouseEnter={() => setPrimaryHover(true)}
              onMouseLeave={() => setPrimaryHover(false)}
              onFocus={() => setPrimaryHover(true)}
              onBlur={() => setPrimaryHover(false)}
              onClick={handleShowModal}
              aria-label="Get started with AI training today"
            >
              <ChartIcon />
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      {/* Bootstrap Modal for Demo Scheduling */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton style={{ 
          borderBottom: '1px solid #e2e8f0', 
          padding: '1.5rem',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
        }}>
          <Modal.Title style={{ 
            fontSize: '1.5rem', 
            fontWeight: '700', 
            color: '#1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            📺 Schedule Your AI Trainer Kit Demo
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={{ padding: '2rem', background: '#ffffff' }}>
          <Form onSubmit={handleSubmit}>
            {/* Form fields remain the same as the original */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Full Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                    style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }}
                    onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Email Address *
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                    style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }}
                    onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Phone Number
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }}
                    onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Organization Name *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your organization name"
                    style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }}
                    onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Your Role *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Professor, IT Director, Procurement Manager"
                    style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }}
                    onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                    Organization Type *
                  </Form.Label>
                  <Form.Select
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleInputChange}
                    required
                    style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease' }}
                    onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  >
                    <option value="">Select Type</option>
                    <option value="university">University</option>
                    <option value="college">College</option>
                    <option value="school">School K-12</option>
                    <option value="training-center">Training Center</option>
                    <option value="corporate">Corporate</option>
                    <option value="government">Government</option>
                    <option value="other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
                Specific Requirements or Questions
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="specificRequirements"
                value={formData.specificRequirements}
                onChange={handleInputChange}
                placeholder="Tell us about your specific needs, curriculum goals, or any questions about our AI training kits..."
                style={{ borderRadius: '8px', border: '2px solid #e2e8f0', padding: '0.75rem', fontSize: '0.875rem', transition: 'border-color 0.2s ease', resize: 'vertical' }}
                onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </Form.Group>

            <div style={{ background: 'linear-gradient(135deg, #e0e7ff 0%, #ede9fe 100%)', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #4f46e5' }}>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#3730a3', lineHeight: '1.4' }}>
                🎯 <strong>What to Expect:</strong> Our product specialists will provide a personalized demo showcasing the AI Trainer Kit features most relevant to your needs, including live demonstrations, pricing options, and implementation roadmap. The demo typically lasts 30-45 minutes.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
              <Button 
                variant="outline-secondary" 
                onClick={handleCloseModal}
                style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', fontWeight: '600', border: '2px solid #e2e8f0', color: '#6b7280' }}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                style={{ padding: '0.75rem 2rem', borderRadius: '8px', fontWeight: '600', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', border: 'none', boxShadow: '0 4px 15px rgba(79, 70, 229, 0.3)' }}
              >
                Schedule Demo
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AITrainerCTA;